import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { toast, Bounce } from "react-toastify"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import DeleteConfirmation from "../../../components/Common/DeleteConfirmation"
import { ICONS } from "../../../constants/icons"
import { del, get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import {
  CITY_SORT_OPTIONS,
  DEFAULT_CITY_SORT_BY,
  DEFAULT_CITY_SORT_DIRECTION,
} from "./adminCitiesData"

const PAGE_SIZE = 15

const getInitials = name =>
  (name || "")
    .split(" ")
    .map(part => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()

const formatMembers = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  return num.toLocaleString("en-US")
}

const mapCityRow = item => {
  const membersCount = Number(item.total_members)
  return {
    id: item.id,
    name: item.city || "--",
    state: item.state || "--",
    country: item.country || "--",
    timezone: item.timezone || "--",
    membersCount: Number.isFinite(membersCount) ? membersCount : 0,
    members: formatMembers(item.total_members),
    activeProjects: formatMembers(item.active_projects),
    addedDate: item.created_at
      ? dayjs(item.created_at).format("MMMM D, YYYY")
      : "--",
    thumbnail: item.thumbnail_path_url || item.cover_media_url || "",
  }
}

/**
 * Admin Cities listing — GET /api/v1/admin/cities
 */
const AdminCities = () => {
  const navigate = useNavigate()
  const { can } = useAdminPermission()
  const canCreate = can("cities", "create")
  const canEdit = can("cities", "edit")
  const canDelete = can("cities", "delete")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_CITY_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_CITY_SORT_DIRECTION,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState(null)
  const [kpi, setKpi] = useState({
    totalCities: "--",
    activeProjects: "--",
  })
  const [isKpiLoading, setIsKpiLoading] = useState(true)
  const [cityToDelete, setCityToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    document.title = "Cities | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, sortBy, sortDirection])

  useEffect(() => {
    let cancelled = false
    const fetchSummary = async () => {
      setIsKpiLoading(true)
      try {
        const response = await get(endpoints.getAdminCitiesSummary)
        if (cancelled) return
        if (response?.data?.success) {
          const result = response.data.result || {}
          setKpi({
            totalCities: formatMembers(result.total_cities),
            activeProjects: formatMembers(result.active_projects),
          })
        } else {
          toastApiError(response, "Failed to load cities summary")
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load cities summary")
        }
      } finally {
        if (!cancelled) setIsKpiLoading(false)
      }
    }
    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [])

  const fetchCities = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await get(endpoints.getAdminCities, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapCityRow)
        setRows(mapped)
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load cities")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load cities")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, debouncedSearch, sortBy, sortDirection])

  useEffect(() => {
    fetchCities()
  }, [fetchCities])

  const kpiCards = useMemo(
    () => [
      {
        key: "totalCities",
        title: "Total Cities",
        value: kpi.totalCities,
        tone: "lightblue-custom",
      },
      {
        key: "activeProjects",
        title: "Active Projects",
        value: kpi.activeProjects,
        tone: "purple-custom",
      },
    ],
    [kpi],
  )

  const handleAddCity = () => {
    navigate("/admin/cities/create")
  }

  const handleViewDetail = row => {
    navigate(`/admin/cities/${row.id}`, {
      state: {
        city: {
          id: row.id,
          name: row.name,
          state: row.state,
          country: row.country,
        },
      },
    })
  }

  const handleEdit = row => {
    navigate(`/admin/cities/${row.id}/edit`, {
      state: {
        city: {
          id: row.id,
          name: row.name,
          state: row.state,
          country: row.country,
          timezone: row.timezone,
        },
      },
    })
  }

  const handleDelete = row => {
    if (row.membersCount > 0) {
      toast.error(`"${row.name}" cannot be deleted while it has members.`, {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
      return
    }
    setCityToDelete(row)
  }

  const closeDeleteModal = () => {
    if (isDeleting) return
    setCityToDelete(null)
  }

  const handleDeleteConfirm = async () => {
    if (!cityToDelete?.id || isDeleting) return

    if (cityToDelete.membersCount > 0) {
      toast.error(
        `"${cityToDelete.name}" cannot be deleted while it has members.`,
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      setCityToDelete(null)
      return
    }

    setIsDeleting(true)
    try {
      const response = await del(endpoints.deleteAdminCity(cityToDelete.id), {
        skipErrorToast: true,
      })

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(
          response,
          `"${cityToDelete.name}" cannot be deleted while it has members.`,
        )
        return
      }

      toast.success(
        response?.data?.message || `"${cityToDelete.name}" deleted.`,
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      setCityToDelete(null)
      await fetchCities()

      try {
        const summaryResponse = await get(endpoints.getAdminCitiesSummary)
        if (summaryResponse?.data?.success) {
          const result = summaryResponse.data.result || {}
          setKpi({
            totalCities: formatMembers(result.total_cities),
            activeProjects: formatMembers(result.active_projects),
          })
        }
      } catch {
        // list refresh already succeeded; KPI can stay stale briefly
      }
    } catch (err) {
      toastApiError(
        err,
        `"${cityToDelete.name}" cannot be deleted while it has members.`,
      )
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExportCities = async () => {
    if (isExporting) return

    setIsExporting(true)
    try {
      const params = {
        sort_by: sortBy,
        sort_direction: sortDirection,
      }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await get(endpoints.exportAdminCities, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export cities")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export cities"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default message
        }
        toast.error(message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `cities-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Cities exported.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
    } catch (err) {
      toastApiError(err, "Failed to export cities")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="admin-page admin-cities">
      <div className="admin-cities__kpi-grid">
        {kpiCards.map(card => (
          <DashboardCard
            key={card.key}
            title={card.title}
            tone={card.tone}
            className="admin-cities__kpi-card"
            fullHeight
          >
            <p className="admin-cities__kpi-value">
              {isKpiLoading ? "…" : card.value}
            </p>
          </DashboardCard>
        ))}
      </div>

      <div className="admin-cities__toolbar">
        <div className="admin-cities__toolbar-left">
          <h1 className="admin-cities__title">Cities</h1>
        </div>

        <div className="admin-cities__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-cities__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "sortBy",
                type: "sortBy",
                label: "Sort By",
                value: sortBy,
                defaultValue: DEFAULT_CITY_SORT_BY,
                onChange: next => {
                  setSortBy(next || DEFAULT_CITY_SORT_BY)
                },
                options: CITY_SORT_OPTIONS,
              },
              {
                key: "sortDirection",
                type: "sortDirection",
                label: "Order",
                value: sortDirection,
                defaultValue: DEFAULT_CITY_SORT_DIRECTION,
                onChange: next => {
                  setSortDirection(next || DEFAULT_CITY_SORT_DIRECTION)
                },
              },
            ]}
          />
          <ExportLogButton
            entityLabel="cities"
            onClick={handleExportCities}
            loading={isExporting}
            disabled={isExporting}
          />
          {canCreate ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={handleAddCity}
            >
              Add City
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className="admin-cities__table-wrapper">
        <div
          className="admin-cities__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div
            className="admin-cities__table-row admin-cities__table-row--head"
            role="row"
          >
            <div role="columnheader">City Name</div>
            <div role="columnheader">State</div>
            <div role="columnheader">Country</div>
            <div role="columnheader">Timezone</div>
            <div role="columnheader">Members</div>
            <div role="columnheader">Active Projects</div>
            <div role="columnheader">Added Date</div>
            <div role="columnheader" className="admin-cities__actions-head">
              Actions
            </div>
          </div>

          {isLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={`city-skel-${index}`}
                  className="admin-cities__table-row admin-cities__table-row--skeleton"
                  role="row"
                >
                  <div className="admin-cities__skeleton" />
                  <div className="admin-cities__skeleton" />
                  <div className="admin-cities__skeleton" />
                  <div className="admin-cities__skeleton" />
                  <div className="admin-cities__skeleton" />
                  <div className="admin-cities__skeleton" />
                  <div className="admin-cities__skeleton" />
                  <div className="admin-cities__skeleton" />
                </div>
              ))
            : null}

          {!isLoading &&
            rows.map(row => (
              <div key={row.id} className="admin-cities__table-row" role="row">
                <div className="admin-cities__user" role="cell">
                  {row.thumbnail ? (
                    <img
                      src={row.thumbnail}
                      alt=""
                      className="admin-cities__avatar admin-cities__avatar--image"
                    />
                  ) : (
                    <div className="admin-cities__avatar" aria-hidden="true">
                      {getInitials(row.name)}
                    </div>
                  )}
                  <TruncatedText className="admin-cities__name">
                    {row.name}
                  </TruncatedText>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-cities__cell"
                  role="cell"
                >
                  {row.state}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-cities__cell"
                  role="cell"
                >
                  {row.country}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-cities__cell"
                  role="cell"
                >
                  {row.timezone}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-cities__cell"
                  role="cell"
                >
                  {row.members}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-cities__cell"
                  role="cell"
                >
                  {row.activeProjects}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-cities__cell"
                  role="cell"
                >
                  {row.addedDate}
                </TruncatedText>
                <div className="admin-cities__actions" role="cell">
                  <CustomButton
                    variant="transparent"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight12}
                    trailingIconAlt=""
                    onClick={() => handleViewDetail(row)}
                  >
                    View Detail
                  </CustomButton>
                  {canEdit ? (
                    <CustomIconButton
                      iconSrc={ICONS.edit}
                      label={`Edit ${row.name}`}
                      size="sm"
                      onClick={() => handleEdit(row)}
                    />
                  ) : null}
                  {canDelete ? (
                    <CustomIconButton
                      iconSrc={ICONS.trash}
                      label={`Delete ${row.name}`}
                      tone="danger"
                      size="sm"
                      onClick={() => handleDelete(row)}
                    />
                  ) : null}
                </div>
              </div>
            ))}

          {!isLoading && !error && rows.length === 0 && (
            <div className="admin-cities__empty">
              <EmptyState
                title="No cities found"
                description="Try adjusting your search or filters."
              />
            </div>
          )}

          {!isLoading && error && rows.length === 0 && (
            <div className="admin-cities__empty">
              <EmptyState
                title="Unable to load cities"
                description={error}
                actionText="Try again"
                onActionClick={fetchCities}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Cities"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-cities__pagination"
      />

      <DeleteConfirmation
        isOpen={Boolean(cityToDelete)}
        onCancel={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Delete City"
        message={
          cityToDelete
            ? `Are you sure you want to delete “${cityToDelete.name}”? This action cannot be undone.`
            : ""
        }
        cancelText="Cancel"
        confirmText="Delete City"
        confirmLoadingText="Deleting..."
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default AdminCities
